import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { Provider } from 'react-redux';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import store from './store/store';
import { NotificationProvider } from './context/NotificationContext';
import './styles/App.css';

import Header from './layouts/Header';
import Footer from './layouts/Footer';
import ProtectedLayout from './layouts/ProtectedLayout';

import Home from './pages/Home';
import Contact from './pages/Contact';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Cookies from './pages/Cookies';
import DPA from './pages/DPA';
import AcceptableUse from './pages/AcceptableUse';
import Dashboard from './pages/Dashboard';

import Welcome from './features/auth/Welcome';
import ProtectedRoute from './features/auth/ProtectedRoute';
import EmailVerificationHandler from './features/auth/EmailVerificationHandler';
import EmailVerificationPending from './features/auth/EmailVerificationPending';
import Logout from './features/auth/Logout';
import AcceptInvitation from './features/auth/AcceptInvitation';
import FlyerPreview from './pages/FlyerPreview';

import Flyers from './features/flyers/Flyers';
import WeeklyAd from './features/flyers/WeeklyAd';
import AddFlyer from './features/flyers/AddFlyer';

import AdTemplates from './features/templates/AdTemplates';
import AdTemplateManager from './features/templates/AdTemplateManager';
import FlyerTemplates from './features/templates/FlyerTemplates';
import FlyerTemplateManager from './features/templates/FlyerTemplateManager';

import Products from './features/products/Products';
import AddProduct from './features/products/AddProduct';
import BulkUploadProducts from './features/products/BulkUploadProducts';

import MasterProductLibrary from './features/master-products/pages/MasterProductLibrary';

import OrganizationSettings from './features/settings/OrganizationSettings';
import LocationManagement from './features/settings/LocationManagement';
import LocationEditor from './features/settings/LocationEditor';
import UserManagement from './features/settings/UserManagement';
import UserEditor from './features/settings/UserEditor';
import OrganizationManagement from './features/settings/OrganizationManagement';
import OrganizationEditor from './features/settings/OrganizationEditor';
import AdminTokenManagement from './features/settings/AdminTokenManagement';
import UniversalProductLibrary from './features/settings/UniversalProductLibrary';

import FlyerAnalysisDashboard from './features/flyer-analysis/FlyerAnalysisDashboard';
import FlyerAnalysisNewJob from './features/flyer-analysis/FlyerAnalysisNewJob';
import FlyerAnalysisBrowse from './features/flyer-analysis/FlyerAnalysisBrowse';
import FlyerAnalysisDetail from './features/flyer-analysis/FlyerAnalysisDetail';
import FlyerAnalysisInsights from './features/flyer-analysis/FlyerAnalysisInsights';

import StoreWebsiteSettings from './features/store-website/StoreWebsiteSettings';
import CMSPageEditor from './features/store-website/pages/CMSPageEditor';
import WebsitePagesPage from './features/store-website/pages/WebsitePagesPage';
import WebsiteSEOPage from './features/store-website/pages/WebsiteSEOPage';
import WebsiteThemePage from './features/store-website/pages/WebsiteThemePage';
import WebsiteSocialPage from './features/store-website/pages/WebsiteSocialPage';
import WebsiteDomainPage from './features/store-website/pages/WebsiteDomainPage';

import ErrorBoundary from './components/error/ErrorBoundary';
import { UserProvider } from './context/UserContext';
import { useAppState } from './hooks/useAppState';

// Redirect /login to home page (handles Auth0 password reset redirect)
function LoginRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/', { replace: true });
  }, [navigate]);
  return null;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// List of protected routes that use the sidebar layout
const PROTECTED_PATHS = [
  '/dashboard',
  '/products',
  '/addproduct',
  '/bulkuploadproducts',
  '/flyers',
  '/addflyer',
  '/flyertemplates',
  '/flyertemplatemanager',
  '/adtemplates',
  '/adtemplatemanager',
  '/masterproducts',
  '/settings',
  '/website',
  '/flyer-analysis',
];

function MainApp() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth0();

  // Run profile fetch at the app level so isInitialized resolves on public
  // routes too — otherwise HeaderBar's auth gate never settles for an
  // authenticated-per-Auth0-cache user sitting on a public page like "/".
  useAppState();

  // Handle email verification errors from Auth0
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const error = params.get('error');
    const errorDescription = params.get('error_description');

    if (error === 'access_denied' && errorDescription?.includes('Please verify your email')) {
      // Clear Auth0's server-side session so "Log In" shows the login screen next time
      logout({
        logoutParams: {
          returnTo: `${window.location.origin}/email-verification-error`,
        },
        openUrl: (url) => {
          // Use navigate-style redirect via the Auth0 logout URL
          window.location.href = url;
        },
      });
    }
  }, [location, navigate, logout]);

  const isProtectedLayout = PROTECTED_PATHS.some((path) =>
    location.pathname.startsWith(path)
  );
  const isFlyerPreview = location.pathname.startsWith('/flyer-preview');

  return (
    <div>
      {!isProtectedLayout && !isFlyerPreview && <Header />}

      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Home />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/weeklyad" element={<WeeklyAd />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/cookies" element={<Cookies />} />
        <Route path="/dpa" element={<DPA />} />
        <Route path="/acceptable-use" element={<AcceptableUse />} />
        <Route path="/login" element={<LoginRedirect />} />
        <Route path="/logout" element={<Logout />} />
        <Route path="/email-verification-error" element={<EmailVerificationHandler />} />
        <Route path="/email-verification-pending" element={<EmailVerificationPending />} />
        <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />

        {/* Public Flyer Preview (used by WhatsApp agent for screenshots) */}
        <Route path="/flyer-preview/:flyerId" element={<FlyerPreview />} />

        {/* Onboarding */}
        <Route
          path="/welcome"
          element={<ProtectedRoute component={Welcome} />}
        />

        {/* Protected Routes with Sidebar Layout */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <Dashboard />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/products"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <Products />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/addproduct"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <AddProduct />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/bulkuploadproducts"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <BulkUploadProducts />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/flyers"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <Flyers />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/addflyer"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <AddFlyer />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/adtemplates"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <AdTemplates />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/adtemplatemanager"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <AdTemplateManager />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/flyertemplates"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <FlyerTemplates />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/flyertemplatemanager"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <FlyerTemplateManager />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/masterproducts"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <MasterProductLibrary />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        {/* Flyer Analysis Routes */}
        <Route
          path="/flyer-analysis"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <FlyerAnalysisDashboard />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/flyer-analysis/new-job"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <FlyerAnalysisNewJob />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/flyer-analysis/browse"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <FlyerAnalysisBrowse />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/flyer-analysis/flyer/:retailer/:date"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <FlyerAnalysisDetail />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/flyer-analysis/insights"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <FlyerAnalysisInsights />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        {/* Settings Routes */}
        <Route
          path="/settings/organization"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <OrganizationSettings />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/organization/:id"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <OrganizationEditor />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/locations"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <LocationManagement />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/locations/new"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <LocationEditor />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/locations/:id"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <LocationEditor />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/users"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <UserManagement />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/users/new"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <UserEditor />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/users/:userId"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <UserEditor />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/organizations"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <OrganizationManagement />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/organizations/new"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <OrganizationEditor />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/organizations/:id"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <OrganizationEditor />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/admin-tokens"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <AdminTokenManagement />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/universal-products"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <UniversalProductLibrary />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/website"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <StoreWebsiteSettings />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/website/pages/:pageId"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <CMSPageEditor />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        {/* Website Routes */}
        <Route
          path="/website/pages"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <WebsitePagesPage />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/website/pages/:pageId"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <CMSPageEditor />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/website/seo"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <WebsiteSEOPage />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/website/theme"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <WebsiteThemePage />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/website/social"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <WebsiteSocialPage />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/website/domain"
          element={
            <ProtectedRoute>
                <ProtectedLayout>
                  <WebsiteDomainPage />
                </ProtectedLayout>
            </ProtectedRoute>
          }
        />
      </Routes>

      {!isProtectedLayout && !isFlyerPreview && location.pathname !== '/' && <Footer />}
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <DndProvider backend={HTML5Backend}>
            <Auth0Provider
              domain={process.env.REACT_APP_AUTH0_DOMAIN}
              clientId={process.env.REACT_APP_AUTH0_CLIENT_ID}
              authorizationParams={{
                redirect_uri: window.location.origin,
                audience: process.env.REACT_APP_AUTH0_AUDIENCE,
              }}
              cacheLocation="localstorage"
              useRefreshTokens
            >
              <UserProvider>
                <NotificationProvider>
                  <Router>
                    <MainApp />
                  </Router>
                </NotificationProvider>
              </UserProvider>
            </Auth0Provider>
          </DndProvider>
        </Provider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
